import type { ActiveCustomerQuery } from '#graphql-operations'
import type { ComputedRef, Ref } from 'vue'
import { until } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { safeRedirectPath } from '../utils/redirect'

export default defineNuxtPlugin((nuxtApp) => {
  // Skip plugin when rendering error page
  if (nuxtApp.payload.error)
    return {}

  const { $localePath } = useNuxtApp()
  const activeCustomerStore = useActiveCustomerStore()
  const { activeCustomer, resolved } = storeToRefs(activeCustomerStore)

  const { refresh: refreshCustomer, clear: clearCustomerRequest, status, error } = useAsyncData(
    'activeCustomer',
    () => activeCustomerStore.fetchActiveCustomer(),
    {
      server: false,
      lazy: true,
      immediate: false,
      getCachedData: () => undefined,
    },
  )

  // Nuxt records a failed refresh in `error` but resolves its promise. Sign-in
  // must see that failure before returning the shopper to their product.
  async function refreshActiveCustomer(): Promise<void> {
    await refreshCustomer({ dedupe: 'cancel' })
    if (error.value)
      throw error.value
  }

  function clearActiveCustomer() {
    clearCustomerRequest()
    activeCustomerStore.clearActiveCustomer()
  }

  function checkInBackground() {
    refreshActiveCustomer().catch(error => console.error('Failed to check the signed-in customer', error))
  }

  // Defer activeCustomer fetch until after LCP and initial critical requests
  if (import.meta.client) {
    // A page or the auth middleware may have asked already: a second request would send the status
    // back to 'pending', and every screen gated on `isAuthResolved` would drop to its loader and back.
    const run = () => {
      if (!resolved.value && status.value !== 'pending')
        checkInBackground()
    }
    nuxtApp.hook('app:mounted', () => {
      // Use longer delay to ensure all critical resources load first
      setTimeout(() => {
        if (typeof requestIdleCallback !== 'undefined')
          requestIdleCallback(run, { timeout: 5000 })
        else
          run()
      }, 1000) // Wait 1 second after mount
    })
  }

  const loggedIn = computed(() => activeCustomer.value != null)
  const isAuthLoading = computed(() => status.value === 'pending')
  // `isAuthLoading` only covers the request being in flight. The fetch is
  // deferred to a second after mount, so for that whole first second the status
  // is still 'idle' and nothing is known yet — which is precisely when a screen
  // must not conclude that the visitor is logged out.
  const isAuthResolved = computed(() => resolved.value || status.value === 'error')

  addRouteMiddleware('auth', (to) => {
    if (!to.meta.auth || loggedIn.value)
      return

    // The active customer is resolved on the client only, so never bounce to the
    // login form on the server or while the check is still in flight. Kick the
    // check off eagerly and let the protected page render a loader until we truly
    // know the auth state — otherwise an already-logged-in user briefly sees the
    // login form before being redirected back to their page.
    if (import.meta.server)
      return

    if (status.value === 'idle')
      checkInBackground()

    if (status.value === 'idle' || status.value === 'pending')
      return

    return {
      path: $localePath('/auth/login'),
      query: { redirectTo: to.path },
    }
  }, { global: true })

  const currentRoute = useRoute()

  if (import.meta.client) {
    // Listen for logout events from other tabs
    if (typeof BroadcastChannel !== 'undefined') {
      const authChannel = new BroadcastChannel('auth')
      // Customers a re-check here found unknown: on a channel (locale) they do not belong to they
      // resolve as a guest, and every hard load of their signed-in tab posts again — without this,
      // each one would re-fetch here for nothing.
      const triedLogins = new Set<string>()
      async function onAuthMessage(event: MessageEvent) {
        if (event.data.type === 'logout') {
          triedLogins.clear()
          const store = useStore()
          const activeOrderStore = useActiveOrderStore()
          store.value.activeCustomer = store.value.activeOrder = null
          clearActiveCustomer()
          await Promise.all([refreshActiveCustomer(), activeOrderStore.refetchActiveOrder()])
        }
        // Another tab signed in, and the session cookie is this tab's too. Left
        // alone, this tab would stay a guest: its saves would go to the browser's
        // guest list and never reach the account, while the signed-in tab showed
        // them as saved. Just as much when this tab is signed in as someone else
        // (a reset or verification link signs the session in as its customer):
        // it would go on showing one customer while every request it makes acts
        // for the other. A tab already showing that customer ignores it, so the
        // re-post of its own check (the watcher below) stops here.
        const showsAlready = (customerId: string) => loggedIn.value && (!customerId || String(activeCustomer.value?.id) === customerId)
        if (event.data.type === 'login' && !showsAlready(String(event.data.customerId ?? ''))) {
          // Not checked yet: the deferred check (mount + 1s + idle) finds the shared session by
          // itself, and a tab still before mount must not fetch the customer mid-hydration.
          if (status.value === 'idle')
            return
          // A check in flight may have left before the sign-in: let it finish instead of
          // cancelling it, and re-check only if it came back as someone else.
          if (status.value === 'pending')
            await until(status).not.toBe('pending')
          const customerId = String(event.data.customerId ?? '')
          if (showsAlready(customerId))
            return
          if (!loggedIn.value && customerId && triedLogins.has(customerId))
            return
          await Promise.all([refreshActiveCustomer(), useActiveOrderStore().refetchActiveOrder()])
          if (!loggedIn.value && customerId)
            triedLogins.add(customerId)
        }
      }
      authChannel.onmessage = (event) => {
        onAuthMessage(event).catch(error => console.error('Failed to sync the signed-in customer between tabs', error))
      }
      // Watched rather than posted from each sign-in path (password, Google,
      // the e-mailed verification and reset links), so none can be missed.
      // A channel never delivers to its own sender.
      // Posted on any change to a customer, not only from a guest: the session
      // moving from one customer to another concerns every other tab as much.
      watch(() => activeCustomer.value?.id, (id, previous) => {
        if (id && String(id) !== String(previous ?? ''))
          authChannel.postMessage({ type: 'login', customerId: String(id) })
      })
      // Cleanup on app unmount
      nuxtApp.hook('app:unmounted', () => {
        authChannel.close()
      })
    }

    // Watch for auth state changes
    watch([loggedIn, isAuthLoading, isAuthResolved], async ([isLoggedIn, isLoading, isResolved]) => {
      // Only redirect if auth is not loading and user is not logged in
      if (currentRoute.meta.auth && isResolved && !isLoggedIn && !isLoading) {
        await navigateTo({
          path: $localePath('/auth/login'),
          query: { redirectTo: currentRoute.path },
        })
      }
    })

    // Watch for successful login to handle redirects
    watch(() => activeCustomer.value?.id, async (customerId) => {
      if (customerId && ['/auth/login', $localePath('/auth/login')].includes(currentRoute.path)) {
        // Honour where the user came from, otherwise land them on their account.
        const to = safeRedirectPath(currentRoute.query.redirectTo, $localePath('/account'))
        await navigateTo(to)
      }
    })
  }

  return {
    provide: {
      auth: { loggedIn, session: activeCustomer, activeCustomer, refreshActiveCustomer, clearActiveCustomer, isAuthLoading, isAuthResolved },
    },
  }
})

export interface AuthPlugin {
  loggedIn: ComputedRef<boolean>
  /** True once the active-customer check has actually completed, either way. */
  isAuthResolved: ComputedRef<boolean>
  session: Ref<ActiveCustomerQuery['activeCustomer']>
  activeCustomer: Ref<ActiveCustomerQuery['activeCustomer']>
  refreshActiveCustomer: () => Promise<void>
  /** Invalidate pending reads and remove the signed-out customer's local state. */
  clearActiveCustomer: () => void
  isAuthLoading: ComputedRef<boolean>
}

declare module '#app' {
  interface NuxtApp {
    $auth: AuthPlugin
  }
  interface RuntimeNuxtHooks {
    /** An explicit sign-out, before the session ends. */
    'auth:logout': () => void | Promise<void>
  }
  interface PageMeta {
    auth?: boolean
  }
}

declare module 'vue' {
  interface ComponentCustomProperties {
    $auth: AuthPlugin
  }
}
